import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star, Copy, ExternalLink, RefreshCcw, Sparkles, Check, MapPin, Zap, Lock,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";

/**
 * Customer-facing review page at /r/[slug]
 * Mobile-first. Per-business white-labeled. 5 steps.
 * Redesigned Feb 2026 to match the Brew & Co. mock: branded blue header,
 * left-aligned card layout, tooltip-style helper cards, and a copy-confirm
 * final screen.
 */
export default function ReviewPage() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(0);
  const [keywords, setKeywords] = useState([]);
  const [optionalText, setOptionalText] = useState("");
  const [generating, setGenerating] = useState(false);
  const [reviewOptions, setReviewOptions] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [selected, setSelected] = useState("");
  const sessionStartedRef = useRef(false);
  const sessionIdRef = useRef("");
  const [hoverRating, setHoverRating] = useState(0);
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [copiedDone, setCopiedDone] = useState(false); // final step 5b confirmation view

  useEffect(() => {
    api.get(`/review/${slug}`).then((r) => {
      if (r.data?.unavailable) { setUnavailable(true); return; }
      setBiz(r.data);
    }).catch(() => setUnavailable(true));
  }, [slug]);

  // Load Manrope on the fly so the review page matches the mocks
  // regardless of what the rest of the app ships.
  useEffect(() => {
    if (document.getElementById("ratezy-manrope")) return;
    const link = document.createElement("link");
    link.id = "ratezy-manrope";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!biz || sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    api.post(`/review/${slug}/session`).then((r) => {
      sessionIdRef.current = r.data.session_id;
    }).catch(() => { });
  }, [biz, slug]);

  // Contextual keywords chosen by rating — up to 15 (9 shown + 6 revealed)
  const contextualKeywords = useMemo(() => {
    if (!biz?.keywords || rating === 0) return [];
    const pos = biz.keywords.filter((k) => k.type === "positive");
    const fb = biz.keywords.filter((k) => k.type === "feedback");
    if (rating >= 4) return pos.slice(0, 15);
    if (rating === 3) return [...pos.slice(0, 8), ...fb.slice(0, 7)];
    return fb.slice(0, 15);
  }, [biz, rating]);

  const visibleKeywords = showAllKeywords ? contextualKeywords : contextualKeywords.slice(0, 9);
  const hiddenCount = Math.max(0, contextualKeywords.length - 9);

  const RATING_LABEL = { 1: "Poor", 2: "Fair", 3: "Okay", 4: "Good", 5: "Excellent" };

  const accent = biz?.primary_colour || "#0055ff";
  const accentSoft = "rgba(0,85,255,0.08)"; // fallback; overridden inline via hexToRgba when biz colour is set
  const hexToRgba = (hex, a) => {
    if (!hex || !hex.startsWith("#")) return `rgba(0,85,255,${a})`;
    const h = hex.length === 4
      ? "#" + hex.slice(1).split("").map((c) => c + c).join("")
      : hex;
    const n = parseInt(h.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  };
  const accentBg = biz ? hexToRgba(accent, 0.08) : accentSoft;
  const accentBorder = biz ? hexToRgba(accent, 0.28) : "rgba(0,85,255,0.28)";

  const totalSteps = 3;

  const generate = async () => {
    if (rating === 0) return;
    setGenerating(true);
    setStep(4);
    setGenProgress(15);
    // fake progressive bar while awaiting the API — capped at 90 until response
    const tick = setInterval(() => {
      setGenProgress((p) => (p >= 90 ? p : p + Math.random() * 12));
    }, 250);
    try {
      const r = await api.post(`/review/${slug}/generate`, {
        rating, keywords, optional_text: optionalText,
        session_id: sessionIdRef.current,
      });
      setReviewOptions(r.data.reviews || []);
      setReviewIdx(0);
      setGenProgress(100);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to generate. Please try again.");
      setStep(3);
    } finally {
      clearInterval(tick);
      setGenerating(false);
    }
  };

  const handleCopyAndSubmit = async () => {
    let copyOk = false;
    try {
      await navigator.clipboard.writeText(selected);
      copyOk = true;
    } catch {
      const ta = document.getElementById("selected-review-textarea");
      if (ta) { ta.select(); try { document.execCommand("copy"); copyOk = true; } catch { /* ignore */ } }
    }
    if (copyOk) {
      api.post(`/review/${slug}/track`, {
        session_id: sessionIdRef.current, event: "copy", selected_review: selected,
      }).catch(() => { });
    }
    setCopiedDone(true);
    // Demo businesses route to a Ratezy demo Google page (no real posting).
    // Live businesses go to the real Google Review URL.
    const destination = biz?.is_demo
      ? `/demo/google?name=${encodeURIComponent(biz?.name || "")}`
      : biz?.google_review_link;
    if (destination) {
      api.post(`/review/${slug}/track`, { session_id: sessionIdRef.current, event: "redirect" }).catch(() => { });
      setTimeout(() => window.open(destination, "_blank", "noopener"), 2000);
    }
  };

  if (unavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="text-center max-w-sm">
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Ratezy</div>
          <div className="text-2xl font-bold mt-2">This review page is currently unavailable.</div>
          <div className="text-sm text-zinc-500 mt-2">Please check back later or contact the business directly.</div>
        </div>
      </div>
    );
  }
  if (!biz) {
    return <div className="min-h-screen flex items-center justify-center bg-white text-zinc-500">Loading...</div>;
  }

  const locationParts = [biz.suburb, biz.state].map((p) => (p || "").trim()).filter(Boolean);
  const locationLine = locationParts.join(", ") || (biz.address || "").trim();

  // Step-agnostic dynamic labels
  const shownStep = copiedDone ? totalSteps : step;

  return (
    <div
      className="bg-white flex flex-col ratezy-review"
      style={{
        fontFamily: "'Manrope', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
        minHeight: "100dvh",
      }}
      data-testid="customer-review-page"
    >
      <style>{`
        .ratezy-review { letter-spacing: -0.01em; }
        .ratezy-review h1, .ratezy-review h2 { letter-spacing: -0.025em; }
        /* Fluid type — scales with the viewport so text stays readable
           on 360px Android phones AND breathes on wider iPhones/tablets. */
        .ratezy-review .rz-h2 { font-size: clamp(21px, 5.8vw, 27px); line-height: 1.18; }
        .ratezy-review .rz-h2-lg { font-size: clamp(25px, 6.8vw, 32px); line-height: 1.1; }
        .ratezy-review .rz-body { font-size: clamp(13px, 3.5vw, 14.5px); }
        .ratezy-review .rz-btn { font-size: clamp(14px, 3.7vw, 15.5px); }
        /* Safari-safe dynamic viewport */
        @supports (min-height: 100svh) {
          .ratezy-review { min-height: 100svh; }
        }
      `}</style>
      {/* ── Branded blue header ─────────────────────────────────────────────── */}
      <div
        className="w-full px-5 pt-4 pb-4 text-white shrink-0"
        style={{ background: accent }}
        data-testid="brand-banner"
      >
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-2.5">
            {/* Logo tile: circular light background containing the uploaded logo */}
            <div
              className={
                biz.logo_url
                  ? "w-16 h-12 rounded-xl flex items-center justify-center shrink-0 p-1"
                  : "w-12 h-12 rounded-full flex items-center justify-center shrink-0"
              }
              style={{ background: biz.logo_url ? "transparent" : "#faf6ec" }}
            >
              {biz.logo_url ? (
                <img
                  src={fileUrl(biz.logo_url.replace("/api/files/", ""))}
                  alt={biz.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-xl font-extrabold" style={{ color: accent }}>
                  {(biz.name || "?").trim().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <h1
                className="text-[18px] sm:text-[21px] font-extrabold leading-tight truncate"
                data-testid="brand-business-name"
              >
                {biz.name}
              </h1>
              {locationLine && (
                <div
                  className="text-xs sm:text-sm opacity-95 mt-0.5 inline-flex items-center gap-1"
                  data-testid="brand-address"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{locationLine}</span>
                </div>
              )}
            </div>
          </div>

          {/* Step label + progress — hidden after generation begins */}
          {step <= 3 && !copiedDone && (
            <>
              <div className="text-xs font-semibold mt-4" data-testid="step-label">
                Step {shownStep} of {totalSteps}
              </div>
              <div
                className="mt-1.5 flex gap-1.5"
                data-testid="progress-bar"
                role="progressbar"
                aria-valuenow={shownStep}
                aria-valuemin={1}
                aria-valuemax={totalSteps}
              >
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className="flex-1 h-1 rounded-full transition-all"
                    style={{
                      background: shownStep >= s
                        ? "#ffffff"
                        : "rgba(255,255,255,0.35)",
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="flex-1 w-full max-w-sm mx-auto px-5 pt-4 pb-3 flex flex-col">
        <div className="flex-1 flex flex-col">
          <AnimatePresence mode="wait">

            {/* STEP 1 — RATING */}
            {step === 1 && !copiedDone && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="text-center flex-1 flex flex-col justify-evenly py-2"
                data-testid="step-rating"
              >

                {/* Heading */}
                <div>
                  <h2 className="rz-h2-lg font-extrabold">Rate your experience</h2>
                  <p className="rz-body text-zinc-600 mt-2">
                    Tap a star to rate your visit.
                  </p>
                </div>

                {/* Rating */}
                <div>
                  <div
                    className="flex justify-center gap-1"
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    {[1, 2, 3, 4, 5].map((s) => {
                      const filled = (hoverRating || rating) >= s;

                      return (
                        <button
                          key={s}
                          data-testid={`star-${s}`}
                          onClick={() => setRating(s)}
                          onMouseEnter={() => setHoverRating(s)}
                          className="p-1 transition-transform active:scale-95"
                        >
                          <Star
                            className="w-10 h-10"
                            strokeWidth={1.5}
                            fill={filled ? accent : "transparent"}
                            color={accent}
                          />
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-3 text-base" data-testid="rating-label">
                    <span className="font-bold" style={{ color: accent }}>
                      {rating || "—"}
                    </span>
                    <span className="text-zinc-500">/5</span>
                    <span className="text-zinc-500"> · </span>
                    <span className="font-medium text-zinc-800">
                      {rating ? RATING_LABEL[rating] : "Choose a rating"}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <PrimaryButton
                    onClick={() => rating > 0 && setStep(2)}
                    disabled={rating === 0}
                    accent={accent}
                    testid="continue-rating"
                  >
                    Continue
                  </PrimaryButton>

                  <TipCard
                    icon={<Zap className="w-3.5 h-3.5" fill={accent} color={accent} />}
                    title="Quick and easy"
                    body="Create your review in a few taps."
                    accent={accent}
                    accentBg={accentBg}
                    className="mt-5 text-left"
                  />
                </div>

              </motion.div>
            )}
            {/* STEP 2 — KEYWORDS */}
            {step === 2 && !copiedDone && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                data-testid="step-keywords"
              >
                <h2 className="rz-h2 font-extrabold">
                  {rating <= 2 ? "What could have been better?" : "What stood out to you?"}
                </h2>

                <p className="rz-body text-zinc-600 mt-1.5">
                  {rating <= 2
                    ? "Choose what could have been better during your visit."
                    : "Choose what stood out in your experience."}
                </p>


                <div className="mt-[clamp(16px,3vh,28px)] flex items-center justify-between">

                  <span className="text-[13px] font-semibold" style={{ color: accent }} data-testid="selected-count">
                    Selected ({keywords.length})
                  </span>
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setShowAllKeywords((v) => !v)}
                      data-testid="toggle-keywords"
                      className="text-[13px] font-semibold hover:underline"
                      style={{ color: accent }}
                    >
                      {showAllKeywords ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>

                {/* 3-column grid of keyword tiles */}
                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  {visibleKeywords.slice(0, 9).map((k) => (
                    <KeywordTile key={k.id || k.label} k={k} active={keywords.includes(k.label)}
                      onClick={() => setKeywords((kw) => kw.includes(k.label)
                        ? kw.filter((x) => x !== k.label)
                        : [...kw, k.label])}
                      accent={accent} accentBg={accentBg} accentBorder={accentBorder} />
                  ))}
                </div>

                {showAllKeywords && contextualKeywords.length > 9 && (
                  <>
                    <hr className="my-3 border-zinc-200" />
                    <div className="grid grid-cols-3 gap-2" data-testid="keywords-more">
                      {contextualKeywords.slice(9).map((k) => (
                        <KeywordTile key={k.id || k.label} k={k} active={keywords.includes(k.label)}
                          onClick={() => setKeywords((kw) => kw.includes(k.label)
                            ? kw.filter((x) => x !== k.label)
                            : [...kw, k.label])}
                          accent={accent} accentBg={accentBg} accentBorder={accentBorder} />
                      ))}
                    </div>
                  </>
                )}

                {contextualKeywords.length === 0 && (
                  <div className="mt-4 text-sm text-zinc-500">
                    No keywords configured — feel free to add details in the next step.
                  </div>
                )}


                <PrimaryButton
                  onClick={() => setStep(3)}
                  accent={accent}
                  testid="continue-keywords"
                  className="mt-[clamp(24px,4vh,40px)]"
                >
                  Continue
                </PrimaryButton>
                <p className="mt-2 text-center text-[12px] text-zinc-500">
                  Your review is private. It only gets posted on Google.
                </p>

                <div className="mt-[clamp(12px,2vh,20px)] text-center">
                  <button
                    onClick={() => setStep(1)}
                    className="text-[13px] font-semibold hover:underline"
                    style={{ color: accent }}
                    data-testid="back-keywords"
                  >
                    Back
                  </button>
                </div>
              </motion.div>
            )}

            {/* STEP 3 — OPTIONAL TEXT */}
            {step === 3 && !copiedDone && (
              <motion.div key="s3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }} data-testid="step-details">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{ background: accentBg, color: accent, border: `1px solid ${accentBorder}` }}
                >
                  <Star className="w-3 h-3" fill={accent} color={accent} /> Optional
                </span>

                <h2 className="rz-h2 font-extrabold mt-2.5">
                  Anything specific you&apos;d like to add?
                </h2>
                <p className="rz-body text-zinc-600 mt-1.5">Add anything else you'd like us to include.</p>

                <div className="relative mt-3">
                  <textarea
                    value={optionalText}
                    onChange={(e) => setOptionalText(e.target.value.slice(0, 250))}
                    placeholder={
                      rating <= 2
                        ? "Example: Slow service, Rude staff, Poor experience..."
                        : rating === 3
                          ? "Example: Good service, Long wait, Friendly staff..."
                          : "Example: Friendly staff, Great service, Nice atmosphere..."
                    }
                    rows={3}
                    data-testid="optional-text"
                    className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-zinc-200 focus:border-zinc-400 outline-none resize-none placeholder:text-zinc-400"
                  />
                  <div className="absolute bottom-2.5 right-3.5 text-xs text-zinc-500" data-testid="optional-count">
                    {optionalText.length}/250
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2.5">
                  <button
                    onClick={() => setStep(2)}
                    data-testid="back-details"
                    className="py-2 rounded-xl text-[13px] font-semibold border-2"
                    style={{ borderColor: accent, color: accent, background: "#fff" }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => { setOptionalText(""); generate(); }}
                    data-testid="skip-details"
                    className="py-2 rounded-xl text-[13px] font-semibold"
                    style={{ color: accent, background: "transparent" }}
                  >
                    Skip
                  </button>
                  <button
                    onClick={generate}
                    disabled={generating}
                    data-testid="generate-reviews"
                    className="min-h-[52px] px-3 rounded-xl text-[13px] font-bold
             transition-all active:scale-[0.98]
             disabled:opacity-50"
                    style={{
                      background: accent,
                      color: "#fff",
                      boxShadow: `0 6px 16px ${accent}40`,
                    }}
                  >
                    Generate →
                  </button>
                </div>

                <TipCard
                  icon={<Star className="w-3.5 h-3.5" fill={accent} color={accent} />}
                  title="Add more detail"
                  body="A short note can make your review more personal."
                  accent={accent}
                  accentBg={accentBg}
                  className="mt-4"
                />
              </motion.div>
            )}

            {/* STEP 4 — GENERATING or CHOOSE */}
            {step === 4 && !copiedDone && (generating || reviewOptions.length === 0) && (
              <motion.div key="s4-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }} className="text-center" data-testid="step-generating">
                <div className="mt-4 flex justify-center">
                  <Sparkles className="w-12 h-12" strokeWidth={1.75} fill={accent} color={accent} />
                </div>
                <h2 className="rz-h2 font-extrabold mt-3">Creating your review options...</h2>
                <p className="rz-body text-zinc-600 mt-1.5">This will only take a few seconds.</p>

                <div className="mt-4 h-1.5 rounded-full bg-zinc-100 overflow-hidden" data-testid="gen-progress">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, Math.round(genProgress))}%`, background: accent }}
                  />
                </div>

                <TipCard
                  icon={<Zap className="w-3.5 h-3.5" fill={accent} color={accent} />}
                  title=""
                  body="We're writing a few options based on your experience."
                  accent={accent}
                  accentBg={accentBg}
                  className="mt-4 text-left"
                />
              </motion.div>
            )}

            {step === 4 && !copiedDone && !generating && reviewOptions.length > 0 && (
              <motion.div key="s4-choose" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }} data-testid="step-reviews">
                <h2 className="rz-h2 font-extrabold">Choose a review option</h2>
                <p className="rz-body text-zinc-600 mt-1.5">Pick the one that sounds most like you.</p>

                {/* Tab pills 1/2/3 */}
                <div className="mt-4 grid grid-cols-3 gap-2" role="tablist" data-testid="review-tabs">
                  {reviewOptions.map((_, i) => (
                    <button key={i}
                      onClick={() => setReviewIdx(i)}
                      data-testid={`review-tab-${i}`}
                      role="tab"
                      aria-selected={reviewIdx === i}
                      className="py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                      style={{
                        background: reviewIdx === i ? accentBg : "#fff",
                        borderColor: reviewIdx === i ? accentBorder : "rgba(0,0,0,0.08)",
                        color: reviewIdx === i ? accent : "#0a0a0a",
                      }}>
                      {i + 1}
                    </button>
                  ))}
                </div>

                {/* Current option card */}
                <div
                  className="mt-3 rounded-2xl border border-zinc-200 p-4"
                  style={{ background: hexToRgba(accent, 0.035) }}
                  data-testid={`review-option-${reviewIdx}`}
                >
                  <span
                    className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                    style={{ background: accentBg, color: accent }}
                    data-testid={`option-label-${reviewIdx}`}
                  >
                    Option {reviewIdx + 1} of {reviewOptions.length}
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-800 whitespace-pre-wrap">
                    {reviewOptions[reviewIdx]}
                  </p>
                </div>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => generate()}
                    disabled={generating}
                    data-testid="regenerate-reviews"
                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
                    style={{ color: accent }}
                  >
                    <RefreshCcw className="w-3.5 h-3.5" /> Generate new options
                  </button>
                </div>

                <PrimaryButton
                  onClick={() => { setSelected(reviewOptions[reviewIdx] || ""); setStep(5); }}
                  accent={accent}
                  testid={`select-review-${reviewIdx}`}
                  className="mt-3"
                >
                  Use this option
                </PrimaryButton>

                <TipCard
                  icon={<Zap className="w-3.5 h-3.5" fill={accent} color={accent} />}
                  title="All options are unique."
                  body="Pick the one that feels most like you."
                  accent={accent}
                  accentBg={accentBg}
                  className="mt-4"
                />
              </motion.div>
            )}

            {/* STEP 5 — DRAFT / COPY */}
            {step === 5 && !copiedDone && (
              <motion.div key="s5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }} data-testid="step-edit-copy">
                <h2 className="rz-h2 font-extrabold">Review your draft</h2>
                <p className="rz-body text-zinc-600 mt-1.5">Make any changes before posting to Google.</p>

                <div className="mt-4">
                  <label className="text-[13px] font-bold" htmlFor="selected-review-textarea">Your draft</label>
                  <div className="relative mt-1.5">
                    <textarea
                      id="selected-review-textarea"
                      value={selected}
                      onChange={(e) => setSelected(e.target.value.slice(0, 500))}
                      rows={5}
                      data-testid="selected-review-textarea"
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border-2 outline-none resize-none"
                      style={{ borderColor: accent }}
                    />
                    <div className="absolute bottom-2.5 right-3.5 text-xs text-zinc-500" data-testid="draft-count">
                      {selected.length} / 500
                    </div>
                  </div>
                </div>

                <PrimaryButton
                  onClick={handleCopyAndSubmit}
                  disabled={!biz.is_demo && !biz.google_review_link}
                  accent={accent}
                  testid="copy-and-submit"
                  className="mt-4"
                >
                  Copy review &amp; open Google
                </PrimaryButton>
                <p className="mt-2 text-center text-[12px] text-zinc-500">
                  We&apos;ll copy your review first, then open Google.
                </p>
                {!biz.is_demo && !biz.google_review_link && (
                  <p className="mt-1.5 text-center text-[11px] text-red-500">Google link not configured for this business.</p>
                )}

                <div className="mt-3 text-center">
                  <button
                    onClick={() => setStep(4)}
                    data-testid="back-draft"
                    className="text-sm font-semibold"
                    style={{ color: accent }}
                  >
                    Back
                  </button>
                </div>
              </motion.div>
            )}

            {/* COPIED CONFIRMATION */}
            {copiedDone && (
              <motion.div key="copied" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }} className="text-center" data-testid="step-copied">
                <div className="mt-3 flex justify-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(34,197,94,0.10)", border: "2px solid #22c55e" }}>
                    <Check className="w-10 h-10" strokeWidth={3} color="#16a34a" />
                  </div>
                </div>
                <h2 className="rz-h2-lg font-extrabold mt-4">Copied!</h2>
                <p className="rz-body text-zinc-600 mt-2 max-w-sm mx-auto">
                  Your review has been copied.<br />Google is opening so you can paste and post it.
                </p>

                <div className="mt-5 rounded-2xl border border-zinc-200 p-3 flex items-center gap-3 text-left">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.05)" }}
                  >
                    <GoogleGlyph />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold">Opening Google review page...</div>
                    <div className="text-[12px] text-zinc-500 mt-0.5">You can now paste your review and hit post.</div>
                  </div>
                </div>

                <PrimaryButton
                  onClick={() => {
                    const destination = biz?.is_demo
                      ? `/demo/google?name=${encodeURIComponent(biz?.name || "")}`
                      : biz?.google_review_link;
                    if (destination) window.open(destination, "_blank", "noopener");
                  }}
                  accent={accent}
                  testid="open-google-now"
                  className="mt-4"
                  icon={<ExternalLink className="w-4 h-4" />}
                >
                  Open Google now
                </PrimaryButton>

                <TipCard
                  icon={<Lock className="w-3.5 h-3.5" color={accent} />}
                  title="Your review is private."
                  body="It only gets posted on Google."
                  accent={accent}
                  accentBg={accentBg}
                  className="mt-3 text-left"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Powered by Ratezy — shared across all screens */}
        <div className="mt-8 flex items-center justify-center gap-2" data-testid="powered-by-ratezy">
          <span className="text-xs font-medium text-zinc-500">Powered by</span>
          <img
            src="/ratezy-logo.webp"
            alt="Ratezy"
            className="block w-auto"
            style={{ height: 18, maxWidth: 90, filter: "invert(1) hue-rotate(180deg)" }}
          />
        </div>
        <div
          className="mt-2 pt-2 border-t border-zinc-200 text-[10px] text-zinc-500 text-center leading-snug"
          data-testid="honesty-footer"
        >
          This tool helps you write your own honest review based on your real
          experience. You can edit or ignore the suggestions before posting.
        </div>
      </div>
    </div>
  );
}

/** Full-width primary CTA button matching mocks. */
function PrimaryButton({ onClick, disabled, accent, testid, children, className = "", icon }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full py-3 rounded-xl rz-btn font-semibold text-white inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-40 ${className}`}
      style={{ background: accent }}
    >
      {icon}
      {children}
    </button>
  );
}

/** Rounded outlined keyword tile with a checkmark when active. */
function KeywordTile({ k, active, onClick, accent, accentBg, accentBorder }) {
  return (
    <button
      onClick={onClick}
      data-testid={`kw-${k.label}`}
      className="relative rounded-xl px-2 py-2 text-[12px] font-semibold text-center transition-all min-h-[46px] flex items-center justify-center border-2 leading-tight"
      style={{
        background: active ? accentBg : "#ffffff",
        borderColor: active ? accent : "rgba(0,0,0,0.10)",
        color: active ? accent : "#0a0a0a",
      }}
    >
      <span className="leading-tight">{k.label}</span>
      {active && (
        <span
          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center shadow-sm"
          style={{ background: accent, color: "#fff" }}
        >
          <Check className="w-2.5 h-2.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

/** Info tooltip card used across steps. */
function TipCard({ icon, title, body, accent, accentBg, className = "" }) {
  return (
    <div
      className={`rounded-2xl px-3 py-2.5 flex items-start gap-2.5 ${className}`}
      style={{ background: accentBg }}
      data-testid="tip-card"
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "#fff", color: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        {title && <div className="text-sm font-bold">{title}</div>}
        <div className={`text-[12.5px] text-zinc-700 ${title ? "mt-0.5" : ""}`}>{body}</div>
      </div>
    </div>
  );
}

/** Simple inline Google "G" mark used on the Copied confirmation card. */
function GoogleGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
